import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  OnDestroy,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { OrAvatarComponent } from '@ui-components';
import { NgClass } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { MessagePosition } from '../../../application/enums';
import { MessageReaction, MessageViewModel } from '../../../application/interfaces';
import { MessageItemViewService } from './view-service/message-item.view-service';
import { SwipeReplyDirective } from './swipe-reply.directive';
import { MessageTextFormatPipe } from './message-text-format.pipe';

@Component({
  selector: 'lib-message-item',
  templateUrl: './message-item.component.html',
  styleUrls: ['./message-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrAvatarComponent, NgClass, IonicModule, SwipeReplyDirective, MessageTextFormatPipe],
  providers: [MessageItemViewService]
})
export class MessageItemComponent implements OnDestroy {
  private readonly viewService: MessageItemViewService = inject(MessageItemViewService);
  private readonly elementRef: ElementRef = inject(ElementRef);

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private reactionLongPressTimer: ReturnType<typeof setTimeout> | null = null;
  private reactionLongPressed: boolean = false;
  private readonly longPressDuration: number = 500;

  readonly message: InputSignal<MessageViewModel> = input.required<MessageViewModel>();
  readonly position: InputSignal<MessagePosition> = input<MessagePosition>(MessagePosition.SINGLE);

  readonly showActions: WritableSignal<boolean> = signal<boolean>(false);
  readonly showMoreActions: WritableSignal<boolean> = signal<boolean>(false);
  readonly showDeleteConfirm: WritableSignal<boolean> = signal<boolean>(false);
  readonly showReactionDetails: WritableSignal<boolean> = signal<boolean>(false);

  readonly isOwnMessage: Signal<boolean> = computed(() => this.viewService.isOwnMessage(this.message()));
  readonly formattedTime: Signal<string> = computed(() => this.viewService.formatTime(this.message().createdAt));
  readonly reactionTypes: { type: string; emoji: string }[] = this.viewService.reactionTypes;
  readonly currentUserId: Signal<string | undefined> = this.viewService.currentUserId;

  readonly MessagePosition: typeof MessagePosition = MessagePosition;

  readonly showHeader: Signal<boolean> = computed(() => {
    return this.position() === MessagePosition.SINGLE || this.position() === MessagePosition.FIRST;
  });

  readonly isLastInGroup: Signal<boolean> = computed(() => {
    return this.position() === MessagePosition.SINGLE || this.position() === MessagePosition.LAST;
  });

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeAllActions();
    }
  }

  ngOnDestroy(): void {
    this.cancelLongPress();
    this.cancelReactionLongPress();
  }

  onTouchStart(): void {
    this.longPressTimer = setTimeout(() => this.toggleActions(), this.longPressDuration);
  }

  onTouchEnd(): void {
    this.cancelLongPress();
  }

  onTouchMove(): void {
    this.cancelLongPress();
  }

  toggleActions(): void {
    this.showActions.update((v: boolean) => !v);
    this.showMoreActions.set(false);
    this.showDeleteConfirm.set(false);
  }

  closeAllActions(): void {
    this.showActions.set(false);
    this.showMoreActions.set(false);
    this.showDeleteConfirm.set(false);
    this.showReactionDetails.set(false);
  }

  toggleMoreActions(event: Event): void {
    event.stopImmediatePropagation();
    this.showMoreActions.update((v: boolean) => !v);
    this.showDeleteConfirm.set(false);
  }

  openImagePreview(imageUrl: string): void {
    this.viewService.openImagePreview(imageUrl);
  }

  onReactionClick(reaction: MessageReaction): void {
    if (this.reactionLongPressed) {
      this.reactionLongPressed = false;
      return;
    }
    this.viewService.toggleReaction(this.message().id, reaction.type, reaction.ownReaction);
  }

  onReactionTouchStart(event: Event): void {
    event.stopPropagation();
    this.reactionLongPressed = false;
    this.reactionLongPressTimer = setTimeout(() => {
      this.reactionLongPressed = true;
      this.showReactionDetails.set(true);
    }, this.longPressDuration);
  }

  onReactionTouchEnd(): void {
    this.cancelReactionLongPress();
  }

  onReactionTouchMove(): void {
    this.cancelReactionLongPress();
  }

  onQuickReaction(reactionType: string): void {
    const existingReaction: MessageReaction | undefined = this.message().reactions.find(
      (r: MessageReaction) => r.type === reactionType
    );
    this.viewService.toggleReaction(this.message().id, reactionType, existingReaction?.ownReaction || false);
    this.closeAllActions();
  }

  getReactionEmoji(type: string): string {
    return this.viewService.getReactionEmoji(type);
  }

  copyText(): void {
    this.viewService.copyToClipboard(this.message().text);
    this.closeAllActions();
  }

  editMessage(): void {
    this.viewService.setEditingMessage(this.message());
    this.closeAllActions();
  }

  quoteMessage(): void {
    this.viewService.setQuotedMessage(this.message());
    this.closeAllActions();
  }

  showDeleteConfirmation(event: Event): void {
    event.stopPropagation();
    this.showDeleteConfirm.set(true);
    this.showMoreActions.set(false);
  }

  confirmDelete(event: Event): void {
    event.stopPropagation();
    this.viewService.deleteMessage(this.message().id);
    this.closeAllActions();
  }

  cancelDelete(event: Event): void {
    event.stopPropagation();
    this.showDeleteConfirm.set(false);
  }

  private cancelLongPress(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private cancelReactionLongPress(): void {
    if (this.reactionLongPressTimer) {
      clearTimeout(this.reactionLongPressTimer);
      this.reactionLongPressTimer = null;
    }
  }
}
